import { motion } from "framer-motion";
import { MapPin, Mail, ExternalLink, Clock, Phone } from "lucide-react";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Via+Cardinale+Cicada+5,+64032+Atri+TE,+Italia";
const MAPS_EMBED = "https://www.google.com/maps?q=Via+Cardinale+Cicada+5,+64032+Atri+TE,+Italia&output=embed";

const HOURS = [
  { d: "Martedì", h: "10:00 — 20:00", p: "Pausa pranzo 13:00 — 14:00" },
  { d: "Mercoledì", h: "10:00 — 20:00", p: "Pausa pranzo 13:00 — 14:00" },
  { d: "Giovedì", h: "10:00 — 20:00", p: "Pausa pranzo 13:00 — 14:00" },
  { d: "Venerdì", h: "09:00 — 20:00", p: "Pausa pranzo 13:00 — 14:00" },
  { d: "Sabato", h: "09:00 — 18:00", p: "Pausa pranzo 13:00 — 14:00" },
  { d: "Domenica & Lunedì", h: "Chiuso", closed: true },
];

export function Location() {
  return (
    <section id="contatti" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-ember">Dove Siamo</p>
          <h2 className="mt-4 font-serif font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-ivory">
            Nel cuore del <span className="italic text-ember">borgo di Atri.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative overflow-hidden rounded-lg border border-white/10 min-h-[380px]"
          >
            <iframe
              title="Mappa Modico Barberia — Via Cardinale Cicada 5, Atri"
              src={MAPS_EMBED}
              className="absolute inset-0 w-full h-full grayscale-[0.4] contrast-[1.05] opacity-90"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            <div className="bg-card border border-white/[0.08] rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-sm bg-ember/10 border border-ember/25 flex items-center justify-center text-ember">
                  <MapPin size={18} />
                </div>
                <div>
                  <p data-testid="location-address-text" className="font-serif text-xl text-ivory leading-snug">
                    Via Cardinale Cicada, 5<br />64032 Atri (TE) — Italia
                  </p>
                  <a
                    data-testid="location-maps-link"
                    href={MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ember hover:text-ember-hover transition-colors duration-300"
                  >
                    Apri in Google Maps <ExternalLink size={13} />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-card border border-white/[0.08] rounded-lg p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 shrink-0 rounded-sm bg-ember/10 border border-ember/25 flex items-center justify-center text-ember">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone">Telefono</p>
                  <a
                    data-testid="location-phone-link"
                    href="tel:+393273832605"
                    className="text-sm sm:text-base text-ivory hover:text-ember transition-colors duration-300"
                  >
                    +39 327 383 2605
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-card border border-white/[0.08] rounded-lg p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 shrink-0 rounded-sm bg-ember/10 border border-ember/25 flex items-center justify-center text-ember">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone">Email</p>
                  <a
                    data-testid="location-email-link"
                    href="mailto:danieledesanctis772@gmail.com"
                    className="text-sm sm:text-base text-ivory hover:text-ember transition-colors duration-300 break-all"
                  >
                    danieledesanctis772@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-card border border-white/[0.08] rounded-lg p-6 flex-1">
              <div className="flex items-center gap-3 text-ember mb-4">
                <Clock size={16} />
                <span className="text-xs font-mono uppercase tracking-[0.25em]">Orari di apertura</span>
              </div>
              <div data-testid="location-hours-table" className="divide-y divide-white/[0.06]">
                {HOURS.map((r) => (
                  <div key={r.d} className="flex items-center justify-between py-2.5 gap-4">
                    <span className="text-sm text-sand">{r.d}</span>
                    <span className="text-right">
                      <span className={`block text-sm font-mono tracking-wide ${r.closed ? "text-stone" : "text-ivory"}`}>
                        {r.h}
                      </span>
                      {r.p && <span className="block text-[10px] font-mono tracking-wide text-stone mt-0.5">{r.p}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
